import { Component, type ErrorInfo, type ReactNode } from "react";
import { trackReactError } from "@/lib/config/appInsights";

interface ErrorBoundaryProps {
	children: ReactNode;
	fallback?: ReactNode;
	name?: string;
	onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
}

export class ErrorBoundary extends Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		const boundaryName = this.props.name ?? "ErrorBoundary";
		console.error(`[${boundaryName}]`, error, errorInfo);
		trackReactError(error, errorInfo, boundaryName);
		this.props.onError?.(error, errorInfo);
	}

	componentDidUpdate(previousProps: ErrorBoundaryProps) {
		if (previousProps.children !== this.props.children && this.state.hasError) {
			this.setState({ hasError: false, error: null });
		}
	}

	render() {
		if (this.state.hasError) {
			if (this.props.fallback) {
				return this.props.fallback;
			}

			return (
				<div className="flex min-h-[220px] items-center justify-center p-24 text-center">
					<div className="flex max-w-md flex-col items-center gap-12 rounded-16 border border-grayscale-200 bg-white p-24 shadow-sm">
						<p className="font-rubik text-sm font-medium text-grayscale-900">
							Something went wrong while rendering this view.
						</p>
						<p className="font-rubik text-xs text-grayscale-600">
							You can retry rendering it without leaving the page.
						</p>
						<button
							type="button"
							onClick={() => this.setState({ hasError: false, error: null })}
							className="rounded-24 bg-primary-600 px-16 py-8 font-rubik text-sm font-medium text-white transition-colors hover:bg-primary-700"
						>
							Try again
						</button>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}
